import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Card,
  Switch,
  Grid,
  Loader,
  Flex,
  Text,
  Image,
  Box,
  ActionIcon,
  TextInput,
  Badge,
} from "@mantine/core";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { IconTrash, IconPlus, IconCheck, IconX } from "@tabler/icons-react";
import { ActionBar } from "@components/shared";
import entityColor from "@util/entityColor";

const iconSize = 16;

export default function AffiliateJoinManagement({
  locationId,
  organizationId,
  userLocationId,
  userOrganizationId,
  affiliateId,
  readOnly = false,
}) {
  const [adding, setAdding] = useState(false);
  const [addedAffiliateIds, setAddedAffiliateIds] = useState([]);
  const [filters, setFilters] = useState({
    search_value: "",
  });

  const queryClient = useQueryClient();

  const reqInfo = [
    { key: "location_id", value: locationId },
    { key: "organization_id", value: organizationId },
    { key: "user_location_id", value: userLocationId },
    { key: "user_organization_id", value: userOrganizationId },
  ]
    .filter((m) => m.value !== null)
    .reduce((acc, cur) => {
      acc[cur.key] = cur.value;
      return acc;
    }, {});

  const {
    data: availableAffiliates,
    isLoading,
    isFetching,
  } = useQuery({
    initialData: [],
    enabled: adding,
    queryKey: [
      "affiliateJoinManagement",
      locationId,
      organizationId,
      userLocationId,
      userOrganizationId,
      adding,
      filters.search_value,
    ],
    queryFn: async () =>
      axios
        .post("/retrieve-affiliates/", {
          page: 0,
          page_size: 100,
          search_value: filters.search_value,
          classification: [1, 2],
          variety: [1, 2, 3],
          status: "active",
        })
        .then(({ data }) => {
          return data.response[0].data;
        })
        .catch((err) => {
          throw err;
        }),
  });

  const addedAffiliateQueryKey = [
    "affiliateJoinManagementAdded",
    locationId,
    organizationId,
    userLocationId,
    userOrganizationId,
    adding,
    filters.search_value,
  ];

  const { data: addedAffiliates, isLoading: isAddedLoading } = useQuery({
    initialData: [],
    enabled: !adding,
    queryKey: addedAffiliateQueryKey,
    queryFn: async () =>
      axios
        .post(
          userLocationId || userOrganizationId
            ? `/retrieve-affiliates/`
            : locationId
            ? `/locations/${locationId}/retrieve-affiliates/`
            : `/organizations/${organizationId}/retrieve-affiliates/`,
          {
            ...reqInfo,
            page: 0,
            page_size: 100,
            search_value: filters.search_value,
            classification: [1, 2],
            variety: [1, 2, 3],
            status: "active",
          }
        )
        .then(({ data }) => {
          setAddedAffiliateIds(data.response[0].data.map((m) => m.id));
          return data.response[0].data;
        })
        .catch((err) => {
          throw err;
        }),
  });

  const fetchAddedAffiliates = () => {
    queryClient.invalidateQueries({ queryKey: addedAffiliateQueryKey });
  };

  const loading = isLoading || isFetching;
  const affiliatesToDisplay = adding ? availableAffiliates : addedAffiliates;
  const isUserJoin = userLocationId || userOrganizationId ? true : false;

  return (
    <div>
      {!isUserJoin && (
        <Box mb="sm">
          <ActionBar color={entityColor.affiliate}>
            <Switch
              checked={adding}
              onChange={(e) => setAdding(!adding)}
              label={"Add Affiliate"}
              size="md"
              color={entityColor.affiliate}
            />
            {loading ? (
              <Loader color={entityColor.affiliate} size="xs" />
            ) : null}
          </ActionBar>
        </Box>
      )}
      {!isUserJoin && (
        <Box>
          <TextInput
            placeholder="Search for an affiliate..."
            size="lg"
            mb="xs"
            value={filters.search_value}
            onChange={(e) =>
              setFilters({ ...filters, search_value: e.target.value })
            }
          />
        </Box>
      )}
      <Grid>
        {/* {!loading && affiliatesToDisplay.length === 0 && (
          <Grid.Col span={12}>
            <EmptyMessage
              title="No affiliates found"
              text="When you add an affiliate, they will appear here."
            />
          </Grid.Col>
        )} */}
        {affiliatesToDisplay?.map((affiliate) => (
          <Grid.Col span={{ base: 12, md: 6 }} key={affiliate.id}>
            <Item
              affiliate={{
                ...affiliate,
                is_user_join: isUserJoin,
              }}
            >
              {!isUserJoin && (
                <>
                  {adding ? (
                    <JoinButton
                      affiliateId={affiliate.id}
                      disabled={addedAffiliateIds.includes(affiliate.id)}
                      reqData={{
                        ...reqInfo,
                        affiliate_id: affiliate.id,
                      }}
                      onSuccess={() =>
                        setAddedAffiliateIds([
                          ...addedAffiliateIds,
                          affiliate.id,
                        ])
                      }
                    />
                  ) : (
                    <JoinButton
                      added
                      affiliateId={affiliate.id}
                      onSuccess={fetchAddedAffiliates}
                      reqData={{
                        ...reqInfo,
                        affiliate_id: affiliate.id,
                      }}
                    />
                  )}
                </>
              )}
            </Item>
          </Grid.Col>
        ))}
      </Grid>
    </div>
  );
}

const Item = ({ affiliate, children }) => {
  return (
    <Card>
      <Flex gap="lg" align={affiliate.is_user_join ? "center" : "flex-start"}>
        {affiliate.logo_url ? (
          <Image
            src={affiliate.logo_url}
            h={50}
            w={50}
            radius="xs"
            fit="contain"
          />
        ) : (
          <Box h={50} w={50} radius="xs" bg="gray.1"></Box>
        )}
        <div style={{ flexGrow: 1 }}>
          <Text
            fw={700}
            component={Link}
            to={`/affiliates/${affiliate.id}/joins`}
          >
            {affiliate.name}
          </Text>
          {children && (
            <Box mt={5}>
              <Flex gap="xs" align="center">
                {children}
              </Flex>
            </Box>
          )}
        </div>
      </Flex>
    </Card>
  );
};

const JoinButton = ({
  reqData,
  onSuccess = () => {},
  added,
  disabled = false,
}) => {
  const [confirm, setConfirm] = useState(false);

  const mutation = useMutation({
    mutationFn: async () =>
      axios
        .post(`/affiliates/associate-loc-org/`, reqData)
        .then(({ data }) => {
          onSuccess();
          return data.response;
        })
        .catch((err) => {
          toast.error(err);
        }),
  });

  if (disabled) {
    return (
      <Badge size="xs" color="gray" variant="light">
        Added
      </Badge>
    );
  }

  if (confirm) {
    return (
      <Flex
        gap="xs"
        align="center"
        p="xs"
        style={{
          background: "var(--mantine-color-gray-1)",
          borderRadius: "0.5em",
          border: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <Text size="xs">You sure?</Text>
        <ActionIcon
          size="sm"
          variant="light"
          color="gray"
          radius="xl"
          title="No"
          onClick={() => setConfirm(false)}
        >
          <IconX size={iconSize} />
        </ActionIcon>
        <ActionIcon
          size="sm"
          variant="light"
          color="gray"
          radius="xl"
          title="Yes"
          onClick={() => mutation.mutate()}
        >
          <IconCheck size={iconSize} />
        </ActionIcon>
      </Flex>
    );
  }

  return (
    <ActionIcon
      size="sm"
      variant="light"
      loading={mutation.isLoading}
      color={"gray"}
      radius="xl"
      title={added ? "Remove" : "Add"}
      p={0}
      onClick={() => {
        if (added) {
          setConfirm(true);
        } else {
          mutation.mutate();
        }
      }}
    >
      {added ? <IconTrash size={iconSize} /> : <IconPlus size={iconSize} />}
    </ActionIcon>
  );
};
